import type { HelpBlock, HelpGuide } from "./guides.js";

/**
 * Search over the Help centre's guides: titles, summaries, section headings,
 * every sentence of the body, and each guide's extra keywords.
 *
 * Every word typed must appear somewhere in a guide for it to match — "send
 * failed" finds the sending guide, not every guide that says "send" — and a
 * word matches the start of a word in the text, so "calib" finds
 * "calibrating". Matches in the title rank above matches in the summary,
 * which rank above matches in the body. Twelve guides of a few hundred words
 * each is small enough to scan in full on every keystroke; there is no index.
 *
 * The reader's apostrophes and dashes vary with their keyboard, so text and
 * query are both folded to plain lower-case words before comparing.
 */

export interface GuideMatch {
  readonly guide: HelpGuide;
  readonly score: number;
  /** The first section whose own text matched, so the list can say where. */
  readonly section: string | null;
}

/** Plain lower-case words: marks, apostrophes and punctuation folded away. */
export function words(text: string): string[] {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\*\*/g, " ")
    .replace(/['\u2018\u2019]/g, "")
    .split(/[^a-z0-9²×.%]+/)
    .map((word) => word.replace(/^\.+|\.+$/g, ""))
    .filter((word) => word.length > 0);
}

function blockText(block: HelpBlock): string {
  switch (block.kind) {
    case "p":
    case "note":
      return block.text;
    case "steps":
    case "list":
      return block.items.join(" ");
  }
}

function hasWord(haystack: readonly string[], needle: string): boolean {
  return haystack.some((word) => word.startsWith(needle));
}

/**
 * The guides that match `query`, best first. An empty query is not a search:
 * it returns every guide, in the order given, with no section.
 */
export function searchGuides(guides: readonly HelpGuide[], query: string): GuideMatch[] {
  const terms = words(query);
  if (terms.length === 0) return guides.map((guide) => ({ guide, score: 0, section: null }));

  const matches: GuideMatch[] = [];
  guides.forEach((guide, order) => {
    const title = words(guide.title);
    const summary = words(guide.summary);
    const keywords = words((guide.keywords ?? []).join(" "));
    const sections = guide.sections.map((section) => ({
      heading: section.heading,
      words: words([section.heading, ...section.blocks.map(blockText)].join(" ")),
    }));
    const everything = [...title, ...summary, ...keywords, ...sections.flatMap((s) => s.words)];

    if (!terms.every((term) => hasWord(everything, term))) return;

    let score = 0;
    for (const term of terms) {
      if (hasWord(title, term)) score += 10;
      else if (hasWord(summary, term) || hasWord(keywords, term)) score += 4;
      else score += 1;
    }
    // Ties keep the guides' own order (the order a reader would take them in).
    score -= order / 1000;

    const section = sections.find((s) => terms.some((term) => hasWord(s.words, term)))?.heading ?? null;
    matches.push({ guide, score, section });
  });
  return matches.sort((a, b) => b.score - a.score);
}
